import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SendIcon from '@mui/icons-material/Send';
import axios from 'src/utils/axios';
import { useSelector } from 'react-redux';
import helpers from 'src/helpers';
import { STORAGE } from 'src/constants';

const CARRIERS = [
  { value: 'att', label: 'AT&T' },
  { value: 'tmobile', label: 'T-Mobile' },
  { value: 'verizon', label: 'Verizon' },
];

const CARRIER_LABEL_MAP = {
  att: 'AT&T',
  tmobile: 'T-Mobile',
  verizon: 'Verizon',
};

const REQUIRES_SOURCE_ID = new Set([
  'partySmartAction', 'groupSmartAction', 'scheduledMessage', 'keyword', 'smartLink',
]);

const STATUS_CHIP_CONFIG = {
  pending: { label: 'Pending', color: '#757575', bgColor: '#eeeeee' },
  delivered: { label: 'Delivered', color: '#2e7d32', bgColor: '#e8f5e9' },
  not_delivered: { label: 'Not Delivered', color: '#c62828', bgColor: '#ffebee' },
  delivered_spam: { label: 'Delivered to Spam', color: '#e65100', bgColor: '#fff3e0' },
  delivered_delayed: { label: 'Delivered Late', color: '#f9a825', bgColor: '#fffde7' },
  unknown: { label: 'Unknown', color: '#757575', bgColor: '#eeeeee' },
  failed: { label: 'Failed', color: '#c62828', bgColor: '#ffebee' },
};

// getPendingDisplayConfig
function getPendingDisplayConfig(createdAt) {
  if (!createdAt) return STATUS_CHIP_CONFIG.pending;
  const elapsedMs = Date.now() - new Date(createdAt).getTime();
  const elapsedMin = elapsedMs / 60000;
  if (elapsedMin >= 30) {
    return { label: 'Filtered', color: '#c62828', bgColor: '#ffcdd2' };
  }
  if (elapsedMin >= 10) {
    return { label: 'Likely Filtered', color: '#c62828', bgColor: '#ffebee' };
  }
  if (elapsedMin >= 5) {
    return { label: 'Possibly Filtered', color: '#e65100', bgColor: '#fff3e0' };
  }
  return STATUS_CHIP_CONFIG.pending;
}

const HISTORY_PAGE_SIZE = 5;
const MAX_ATTACHMENT_SIZE_BYTES = 307200; // 300 KB

// formatRelativeDate
// < 24h ago -> relative ("2 hours ago"), otherwise absolute ("Mar 24, 3:34 PM")
function formatRelativeDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) {
    return `${diffMins}m ago`;
  }
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  }).concat(
    ', ',
    date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    }),
  );
}

// getResultChipIcon
function getResultChipIcon(status) {
  if (status === 'delivered') {
    return <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />;
  }
  if (status === 'pending') {
    return <AccessTimeIcon sx={{ fontSize: 14 }} />;
  }
  return <ErrorOutlineIcon sx={{ fontSize: 14 }} />;
}

function TestDeliverabilityTab({
  open,
  messageBody,
  messageBodyRaw = '',
  attachmentFileName = null,
  attachmentSizeBytes = null,
  attachmentBlobName = null,
  attachmentContainer = null,
  sourceType = 'other',
  sourceId = null,
  // eslint-disable-next-line no-unused-vars
  salespersonId = null,
  messageScore = null,
  scoreData = null,
  defaultCarrier = null,
  unsupportedCarrierName = null,
}) {
  const {
    user,
    azAccountName,
    databaseBeingUsed,
  } = useSelector((state) => state?.account || {});

  const needsSaveFirst = REQUIRES_SOURCE_ID.has(sourceType) && !sourceId;
  const [selectedCarriers, setSelectedCarriers] = useState(
    defaultCarrier ? [defaultCarrier] : [],
  );
  const [sending, setSending] = useState(false);
  const [testId, setTestId] = useState(null); // eslint-disable-line no-unused-vars
  const [testResults, setTestResults] = useState(null);
  const [testError, setTestError] = useState(null);
  const [carrierStatus, setCarrierStatus] = useState({});
  const pollIntervalRef = useRef(null);
  const statusIntervalRef = useRef(null);

  // Past tests state
  const [pastTests, setPastTests] = useState([]);
  const [pastTestsTotal, setPastTestsTotal] = useState(0);
  const [pastTestsOffset, setPastTestsOffset] = useState(0);
  const [pastTestsLoading, setPastTestsLoading] = useState(false);

  const clearPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  const clearStatusPolling = useCallback(() => {
    if (statusIntervalRef.current) {
      clearInterval(statusIntervalRef.current);
      statusIntervalRef.current = null;
    }
  }, []);

  const fetchCarrierStatus = useCallback(async () => {
    try {
      const response = await axios.get('/api/message-tests/carrier-status');
      setCarrierStatus(response.data || {});
    } catch (err) {
      console.error('Error fetching carrier status:', err);
    }
  }, []);

  const fetchPastTests = useCallback(async (offset = 0, append = false) => {
    if (!sourceType || !sourceId) return;
    setPastTestsLoading(true);
    try {
      const response = await axios.get('/api/message-tests/history', {
        params: {
          sourceType,
          sourceId,
          limit: HISTORY_PAGE_SIZE,
          offset,
        },
      });
      const { tests = [], total = 0 } = response.data || {};
      setPastTests((prev) => (append ? [...prev, ...tests] : tests));
      setPastTestsTotal(total);
      setPastTestsOffset(offset);
    } catch (err) {
      console.error('Error fetching past tests:', err);
    } finally {
      setPastTestsLoading(false);
    }
  }, [sourceType, sourceId]);

  // Fetch carrier status when tab opens, poll every 60s
  useEffect(() => {
    if (open) {
      fetchCarrierStatus();
      statusIntervalRef.current = setInterval(fetchCarrierStatus, 60000);
    } else {
      clearStatusPolling();
    }
    return () => clearStatusPolling();
  }, [open, fetchCarrierStatus, clearStatusPolling]);

  // Fetch past tests when tab opens
  useEffect(() => {
    if (open && sourceType && sourceId) {
      fetchPastTests(0, false);
    }
  }, [open, sourceType, sourceId, fetchPastTests]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      clearPolling();
      setTestId(null);
      setTestResults(null);
      setTestError(null);
      setSending(false);
      setSelectedCarriers(defaultCarrier ? [defaultCarrier] : []);
      setCarrierStatus({});
      setPastTests([]);
      setPastTestsTotal(0);
      setPastTestsOffset(0);
    }
  }, [open, clearPolling]);

  // Clean up polling on unmount
  useEffect(() => () => {
    clearPolling();
    clearStatusPolling();
  }, [clearPolling, clearStatusPolling]);

  const handleCarrierToggle = (carrier) => {
    setSelectedCarriers((prev) => {
      if (prev.includes(carrier)) {
        return prev.filter((c) => c !== carrier);
      }
      return [...prev, carrier];
    });
  };

  const hasPendingResults = testResults
    && testResults.some((r) => r.status === 'pending');

  const pollResults = useCallback(async (id) => {
    try {
      const response = await axios.get(`/api/message-tests/${id}`);
      const results = response.data?.testResults || [];
      setTestResults(results);

      const stillPending = results.some((r) => r.status === 'pending');
      if (!stillPending) {
        clearPolling();
        // Refresh past tests now that this test is complete
        if (sourceType && sourceId) {
          fetchPastTests(0, false);
        }
      }
    } catch (err) {
      console.error('Error polling test results:', err);
    }
  }, [clearPolling, sourceType, sourceId, fetchPastTests]);

  const handleSendTest = async () => {
    setSending(true);
    setTestError(null);
    setTestResults(null);
    setTestId(null);

    const hasAttachment = !!(attachmentFileName || attachmentBlobName);
    const messageType = (hasAttachment || (messageBody || '').length > 160)
      ? 'mms'
      : 'sms';
    const characterCount = (messageBody || '').length;
    const segmentCount = Math.ceil(characterCount / 160) || 1;

    try {
      const response = await axios.post('/api/message-tests', {
        messageBodyRaw,
        messageBody,
        messageType,
        characterCount,
        segmentCount,
        attachments: hasAttachment
          ? [{
            type: 'file',
            extension: (attachmentFileName || attachmentBlobName || '')
              .split('.').pop() || null,
            sizeKb: attachmentSizeBytes
              ? Math.round(attachmentSizeBytes / 1024)
              : null,
          }]
          : [],
        carriersSelected: selectedCarriers.filter((c) => carrierStatus[c]?.online),
        messageScore: scoreData?.overallScore || messageScore,
        scoreData: scoreData
          ? {
            improvements: scoreData.opportunities,
            lookingGood: scoreData.highlights,
          }
          : null,
        sourceType,
        sourceId,
        attachmentBlobName,
        attachmentContainer,
      });

      const newTestId = response.data?.id;
      setTestId(newTestId);
      setTestResults(response.data?.testResults || []);

      // Start polling
      if (newTestId) {
        pollIntervalRef.current = setInterval(() => {
          pollResults(newTestId);
        }, 15000);
      }
    } catch (err) {
      console.error('Error sending test:', err);
      const status = err?.response?.status;
      if (status === 403) {
        setTestError('You do not have permission to use Test Deliverability.');
      } else {
        setTestError(
          err?.response?.data?.error || 'Failed to send test. Please try again.',
        );
      }
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '\u2014';
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleShowMore = () => {
    const nextOffset = pastTestsOffset + HISTORY_PAGE_SIZE;
    fetchPastTests(nextOffset, true);
  };

  const hasMorePastTests = pastTestsTotal > pastTestsOffset + HISTORY_PAGE_SIZE;

  // renderPastTestRow
  const renderPastTestRow = (test) => {
    const rawBody = test.messageBodyRaw || '';
    const preview = rawBody.length > 60
      ? `${rawBody.slice(0, 60)}...`
      : rawBody;
    const attachmentArr = test.attachments || [];
    const firstAttachment = attachmentArr[0];
    const attachSizeKb = firstAttachment?.sizeKb
      || (firstAttachment?.sizeBytes
        ? Math.round(firstAttachment.sizeBytes / 1024)
        : null);
    const isLargeAttachment = firstAttachment?.sizeBytes
      > MAX_ATTACHMENT_SIZE_BYTES;
    const attachFileName = firstAttachment?.blobName || 'file';

    return (
      <Box
        key={test.id}
        sx={{
          mb: 0.75,
          px: 1.25,
          py: 1,
          borderRadius: 1,
          backgroundColor: '#fafafa',
          border: '1px solid #e0e0e0',
        }}
      >
        {/* Line 1: Date + attachment + chars + score */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 0.25,
        }}
        >
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', fontSize: '11px' }}
          >
            {formatRelativeDate(test.createdAt)}
          </Typography>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
          }}
          >
            {firstAttachment && (
              <Tooltip
                title={`Attachment: ${attachFileName} (${attachSizeKb || '?'} KB)`}
                placement="top"
                arrow
              >
                <Typography
                  variant="caption"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.25,
                    color: isLargeAttachment ? '#e65100' : '#2e7d32',
                    fontWeight: 500,
                    fontSize: '11px',
                    cursor: 'default',
                  }}
                >
                  <AttachFileIcon sx={{ fontSize: 12 }} />
                  {attachSizeKb ? `${attachSizeKb} KB` : 'file'}
                </Typography>
              </Tooltip>
            )}
            {test.characterCount != null && (
              <Tooltip
                title={`Message length: ${test.characterCount} characters`}
                placement="top"
                arrow
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '11px',
                    cursor: 'default',
                  }}
                >
                  {`${test.characterCount} chars`}
                </Typography>
              </Tooltip>
            )}
            {test.messageScore != null && (
              <Tooltip
                title={`Message Score: ${test.messageScore}/100`}
                placement="top"
                arrow
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '11px',
                    cursor: 'default',
                  }}
                >
                  {`${test.messageScore}/100`}
                </Typography>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Line 2: Message preview with optional attachment thumbnail */}
        <Tooltip
          title={(
            <Box>
              <span style={{ whiteSpace: 'pre-line' }}>
                {rawBody}
              </span>
              {firstAttachment?.blobName
                && firstAttachment?.mimeType?.startsWith('image/') && (
                <img
                  src={helpers.azure.getImageFileURL(
                    firstAttachment.blobName,
                    user?.id,
                    azAccountName,
                    STORAGE.sas,
                    databaseBeingUsed,
                    firstAttachment.container,
                    true,
                  )}
                  alt=""
                  style={{
                    display: 'block',
                    maxWidth: 150,
                    maxHeight: 150,
                    borderRadius: 4,
                    marginTop: 8,
                  }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
            </Box>
          )}
          placement="top"
          arrow
        >
          <Typography
            variant="body2"
            sx={{
              color: '#546e7a',
              fontSize: '11px',
              lineHeight: 1.3,
              mb: 0.5,
              cursor: 'default',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {preview || '(no message)'}
          </Typography>
        </Tooltip>

        {/* Line 3: Carrier chips */}
        <Box sx={{
          display: 'flex',
          gap: 0.5,
          flexWrap: 'wrap',
        }}
        >
          {(test.testResults || []).map((result) => {
            const chipConfig = result.status === 'pending'
              ? getPendingDisplayConfig(test.createdAt)
              : (STATUS_CHIP_CONFIG[result.status] || STATUS_CHIP_CONFIG.unknown);
            const carrierLabel = CARRIER_LABEL_MAP[result.carrier]
              || result.carrier;
            const statusLabel = chipConfig.label;
            const latencyStr = result.latencyMs
              ? ` in ${(result.latencyMs / 1000).toFixed(1)}s`
              : '';
            const chipTip = `${carrierLabel}: ${statusLabel}${latencyStr}`;

            return (
              <Tooltip
                key={result.id}
                title={chipTip}
                placement="top"
                arrow
              >
                <Chip
                  icon={getResultChipIcon(result.status)}
                  label={carrierLabel}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '10px',
                    color: chipConfig.color,
                    backgroundColor: chipConfig.bgColor,
                    border: `1px solid ${chipConfig.color}`,
                    '& .MuiChip-icon': {
                      color: chipConfig.color,
                      marginLeft: '3px',
                    },
                  }}
                />
              </Tooltip>
            );
          })}
        </Box>
      </Box>
    );
  };

  // renderPastTests
  const renderPastTests = () => {
    if (!pastTests.length && !pastTestsLoading) return null;

    return (
      <Box sx={{ mt: 2.5 }}>
        <Divider sx={{ mb: 1.5 }} />
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, mb: 1, color: '#37474f' }}
        >
          Past Tests
        </Typography>

        {pastTests.map(renderPastTestRow)}

        {pastTestsLoading && (
          <Box sx={{ textAlign: 'center', py: 1 }}>
            <CircularProgress size={18} />
          </Box>
        )}

        {hasMorePastTests && !pastTestsLoading && (
          <Button
            size="small"
            onClick={handleShowMore}
            sx={{
              textTransform: 'none',
              fontSize: '12px',
              color: 'text.secondary',
            }}
            fullWidth
          >
            Show more
          </Button>
        )}
      </Box>
    );
  };

  return (
    <Box>
      {/* Carrier Selection + Send Test */}
      {!testResults && (
        <>
          {(defaultCarrier || unsupportedCarrierName) && (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 1.5,
            }}
            >
              <Typography variant="body2" sx={{ color: '#37474f' }}>
                Sent to
              </Typography>
              <Chip
                label={
                  defaultCarrier
                    ? (CARRIER_LABEL_MAP[defaultCarrier] || defaultCarrier)
                    : unsupportedCarrierName
                }
                size="small"
                sx={{
                  fontSize: '11px',
                  height: 22,
                  backgroundColor: '#e3f2fd',
                  color: '#1565c0',
                }}
              />
            </Box>
          )}
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, mb: 1, color: '#37474f' }}
          >
            Select Carriers
          </Typography>
          <FormGroup row sx={{ flexWrap: 'wrap', mb: 2 }}>
            {CARRIERS.map((carrier) => {
              const status = carrierStatus[carrier.value];
              const isOnline = status?.online ?? true;
              const isDisabled = sending || !isOnline;

              const label = (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: isOnline ? '#4caf50' : '#f44336',
                      flexShrink: 0,
                    }}
                  />
                  {carrier.label}
                </Box>
              );

              const checkbox = (
                <FormControlLabel
                  key={carrier.value}
                  control={(
                    <Checkbox
                      checked={isOnline && selectedCarriers.includes(carrier.value)}
                      onChange={() => handleCarrierToggle(carrier.value)}
                      size="small"
                      disabled={isDisabled}
                    />
                  )}
                  label={label}
                  sx={{ flexShrink: 1 }}
                />
              );

              if (!isOnline) {
                return (
                  <Tooltip
                    key={carrier.value}
                    title={`${carrier.label} is currently offline`}
                    arrow
                  >
                    <span>{checkbox}</span>
                  </Tooltip>
                );
              }

              return checkbox;
            })}
          </FormGroup>

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontStyle: 'italic',
              color: 'text.secondary',
              mb: 2,
            }}
          >
            Test messages are billed to your account.
          </Typography>

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontStyle: 'italic',
              color: '#9e9e9e',
              mb: 2,
            }}
          >
            A successful test indicates current deliverability, but does not
            guarantee future delivery. Carriers may change their filtering
            criteria at any time.
          </Typography>

          {unsupportedCarrierName && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: '#e65100',
                mb: 2,
              }}
            >
              {`This number appears to be on ${unsupportedCarrierName}, which we don\u2019t currently support for testing. You can still test with other carriers.`}
            </Typography>
          )}

          {needsSaveFirst && (
            <Typography
              variant="body2"
              sx={{ color: '#e65100', mb: 2 }}
            >
              Please save this action before testing deliverability.
            </Typography>
          )}

          {testError && (
            <Typography
              variant="body2"
              sx={{ color: '#c62828', mb: 2 }}
            >
              {testError}
            </Typography>
          )}

          <Button
            variant="contained"
            color="primary"
            startIcon={
              sending
                ? <CircularProgress size={16} color="inherit" />
                : <SendIcon />
            }
            onClick={handleSendTest}
            disabled={
              needsSaveFirst
              || sending
              || selectedCarriers.filter((c) => carrierStatus[c]?.online).length === 0
              || !(messageBody || '').trim()
            }
            fullWidth
          >
            {sending ? 'Sending...' : 'Send Test'}
          </Button>
        </>
      )}

      {/* Current Test Results */}
      {testResults && (
        <Box>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, mb: 2, color: '#37474f' }}
          >
            Test Results
            {hasPendingResults && (
              <CircularProgress
                size={14}
                sx={{ ml: 1, verticalAlign: 'middle' }}
              />
            )}
          </Typography>

          {testResults.map((result) => {
            const chipConfig = result.status === 'pending'
              ? getPendingDisplayConfig(result.createdAt)
              : (STATUS_CHIP_CONFIG[result.status] || STATUS_CHIP_CONFIG.unknown);
            const carrierInfo = CARRIERS.find((c) => c.value === result.carrier);

            return (
              <Box
                key={result.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  mb: 1.5,
                  p: 1.5,
                  borderRadius: 1,
                  backgroundColor: '#fafafa',
                  border: '1px solid #e0e0e0',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, flexShrink: 1, mr: 1 }}
                >
                  {carrierInfo?.label || result.carrier}
                </Typography>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexShrink: 1,
                  flexWrap: 'wrap',
                }}
                >
                  <Chip
                    label={chipConfig.label}
                    size="small"
                    sx={{
                      color: chipConfig.color,
                      backgroundColor: chipConfig.bgColor,
                      fontWeight: 500,
                      border: `1px solid ${chipConfig.color}`,
                    }}
                  />
                  {result.receivedAt && (
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary' }}
                    >
                      {formatTime(result.receivedAt)}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}

          {hasPendingResults && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                fontStyle: 'italic',
                color: 'text.secondary',
                mt: 1,
                textAlign: 'center',
              }}
            >
              Checking for results every 15 seconds...
            </Typography>
          )}
          {hasPendingResults && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                fontStyle: 'italic',
                color: 'text.secondary',
                mt: 0.5,
                textAlign: 'center',
              }}
            >
              You can close this window and come back later to see results.
            </Typography>
          )}

          {!hasPendingResults && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setTestResults(null);
                setTestId(null);
                setTestError(null);
              }}
              sx={{ mt: 2 }}
              fullWidth
            >
              Run Another Test
            </Button>
          )}
        </Box>
      )}

      {/* Past Tests — always visible at bottom */}
      {renderPastTests()}
    </Box>
  );
}

TestDeliverabilityTab.propTypes = {
  open: PropTypes.bool.isRequired,
  messageBody: PropTypes.string.isRequired,
  messageBodyRaw: PropTypes.string,
  attachmentFileName: PropTypes.string,
  attachmentSizeBytes: PropTypes.number,
  attachmentBlobName: PropTypes.string,
  attachmentContainer: PropTypes.string,
  sourceType: PropTypes.string,
  sourceId: PropTypes.number,
  salespersonId: PropTypes.number,
  messageScore: PropTypes.number,
  scoreData: PropTypes.shape({
    overallRating: PropTypes.string,
    overallScore: PropTypes.number,
    opportunities: PropTypes.array,
    highlights: PropTypes.array,
  }),
  defaultCarrier: PropTypes.string,
  unsupportedCarrierName: PropTypes.string,
};

export default TestDeliverabilityTab;
